import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  MessageCircle, Send, ArrowLeft, MoreVertical, Archive, Trash2, Flag, Inbox as InboxIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ReportDialog from "../components/ReportDialog";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR");

export function Inbox() {
  const [convs, setConvs] = useState([]);
  const [view, setView] = useState("active"); // active | archived
  const [confirmDel, setConfirmDel] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const reload = () => api.get("/conversations", { params: { include_archived: view === "archived" } })
    .then((r) => setConvs(view === "archived" ? r.data.filter((c) => c.archived) : r.data));
  useEffect(() => { reload(); const id = setInterval(reload, 15000); return () => clearInterval(id);
    // eslint-disable-next-line
  }, [view]);

  const archive = async (partnerId) => {
    try { await api.post(`/conversations/${partnerId}/archive`); toast.success("Conversation archivée"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const unarchive = async (partnerId) => {
    try { await api.post(`/conversations/${partnerId}/unarchive`); toast.success("Conversation restaurée"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/conversations/${confirmDel.partner.user_id}`);
      toast.success("Conversation supprimée"); setConfirmDel(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
        <MessageCircle className="w-7 h-7 text-sky-700" />
        Messagerie
      </h1>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setView("active")}
          className={`text-xs px-3 py-1.5 rounded-full border ${view === "active" ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="conv-view-active">
          <InboxIcon className="inline w-3 h-3 mr-1" />Conversations
        </button>
        <button onClick={() => setView("archived")}
          className={`text-xs px-3 py-1.5 rounded-full border ${view === "archived" ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="conv-view-archived">
          <Archive className="inline w-3 h-3 mr-1" />Archivées
        </button>
      </div>
      {convs.length === 0 ? (
        <div className="card-soft p-8 text-center text-slate-500">
          {view === "archived" ? "Aucune conversation archivée." : "Aucune conversation pour l'instant. Cliquez sur \"Envoyer un message\" depuis la fiche d'un MNS pour démarrer."}
        </div>
      ) : (
        <div className="grid gap-2" data-testid="conversations">
          {convs.map((c) => (
            <div key={c.partner.user_id} className="card-soft p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-all relative" data-testid={`conv-${c.partner.user_id}`}>
              <Link to={`/messages/${c.partner.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <img src={c.partner.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.partner.name)}&background=0369A1&color=fff`} alt="" className="w-12 h-12 rounded-full ring-2 ring-sky-200" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {c.partner.name}
                      {c.archived && <span className="text-[10px] uppercase bg-slate-200 text-slate-600 rounded-full px-1.5 py-0.5">Archivée</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 whitespace-nowrap">{fmtDate(c.last_message.created_at)}</div>
                  </div>
                  <div className="text-sm text-slate-600 truncate">
                    {c.last_message.from_user_id === c.partner.user_id ? "" : "Vous: "}{c.last_message.body}
                  </div>
                </div>
              </Link>
              {c.unread > 0 && <span className="bg-sky-700 text-white text-xs rounded-full px-2 py-0.5">{c.unread}</span>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="rounded-full" data-testid={`conv-menu-${c.partner.user_id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {c.archived ? (
                    <DropdownMenuItem onClick={() => unarchive(c.partner.user_id)} data-testid={`unarchive-${c.partner.user_id}`}>
                      <Archive className="w-4 h-4 mr-2" />Restaurer
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => archive(c.partner.user_id)} data-testid={`archive-${c.partner.user_id}`}>
                      <Archive className="w-4 h-4 mr-2" />Archiver
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setReportTarget(c.partner)} data-testid={`report-${c.partner.user_id}`}>
                    <Flag className="w-4 h-4 mr-2" />Signaler
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600" onClick={() => setConfirmDel(c)} data-testid={`delete-conv-${c.partner.user_id}`}>
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cette conversation ?</DialogTitle>
            <DialogDescription>
              Vous ne verrez plus les messages avec <strong>{confirmDel?.partner.name}</strong>.
              Si l'autre personne vous envoie un nouveau message, la conversation réapparaîtra automatiquement.
              Vous pouvez aussi simplement <strong>archiver</strong> au lieu de supprimer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDel(null)}>Annuler</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={doDelete} data-testid="delete-conv-confirm">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reportTarget && (
        <ReportDialog
          open={true}
          onClose={() => setReportTarget(null)}
          targetType={reportTarget.role === "mns" ? "mns" : "client"}
          targetId={reportTarget.user_id}
          targetLabel={reportTarget.name}
        />
      )}
    </div>
  );
}

export function Conversation() {
  const { id: partnerId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ partner: null, messages: [] });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [gatedNotice, setGatedNotice] = useState(null);
  const endRef = useRef(null);

  const reload = () => api.get(`/messages`, { params: { with_user_id: partnerId } }).then((r) => setData(r.data));
  useEffect(() => {
    reload();
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [partnerId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data.messages]);

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post("/messages", { to_user_id: partnerId, body });
      setDraft(""); setGatedNotice(null); reload();
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (err.response?.status === 403) setGatedNotice(detail);
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const archive = async () => {
    try { await api.post(`/conversations/${partnerId}/archive`); toast.success("Conversation archivée"); nav("/messages"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const deleteConv = async () => {
    if (!window.confirm("Supprimer cette conversation ? Elle réapparaîtra si l'autre vous écrit.")) return;
    try { await api.delete(`/conversations/${partnerId}`); toast.success("Conversation supprimée"); nav("/messages"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!data.partner) return <div className="p-12 text-center text-slate-500">Chargement...</div>;
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col h-[calc(100vh-100px)]">
      <div className="card-soft p-4 mb-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => nav("/messages")}><ArrowLeft className="w-5 h-5" /></Button>
        <Link
          to={data.partner.role === "mns" ? `/mns/${data.partner.user_id}` : `/u/${data.partner.user_id}`}
          className="flex items-center gap-3 group flex-1 min-w-0"
          title={`Voir le profil de ${data.partner.name}`}
          data-testid={`partner-profile-link-${data.partner.user_id}`}
        >
          <img src={data.partner.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.partner.name)}&background=0369A1&color=fff`} alt="" className="w-10 h-10 rounded-full ring-2 ring-sky-200 group-hover:ring-sky-400 transition" />
          <div className="min-w-0">
            <div className="font-semibold group-hover:text-sky-700 truncate">{data.partner.name}</div>
            <div className="text-xs text-slate-500 capitalize">{data.partner.role === "client" ? "Élève" : data.partner.role}</div>
          </div>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="rounded-full" data-testid="conv-menu">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={archive} data-testid="archive-current"><Archive className="w-4 h-4 mr-2" />Archiver</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="report-current"><Flag className="w-4 h-4 mr-2" />Signaler</DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={deleteConv} data-testid="delete-current"><Trash2 className="w-4 h-4 mr-2" />Supprimer</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {gatedNotice && (
        <div className="card-soft p-4 mb-3 bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="conv-gated-notice">
          🔒 {gatedNotice}
        </div>
      )}

      <div className="flex-1 card-soft p-4 overflow-auto space-y-2" data-testid="message-list">
        {data.messages.length === 0 && <div className="text-center text-slate-400 text-sm py-8">Aucun message. Brisez la glace !</div>}
        {data.messages.map((m) => {
          const mine = m.from_user_id === user.user_id;
          const senderPicture = mine
            ? null
            : (data.partner.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.partner.name)}&background=0369A1&color=fff`);
          return (
            <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"} items-end gap-2`}>
              {!mine && (
                <Link
                  to={data.partner.role === "mns" ? `/mns/${data.partner.user_id}` : `/u/${data.partner.user_id}`}
                  className="shrink-0"
                  title={`Voir le profil de ${data.partner.name}`}
                  data-testid={`msg-avatar-${m.msg_id}`}
                >
                  <img src={senderPicture} alt="" className="w-7 h-7 rounded-full ring-1 ring-sky-200 hover:ring-sky-400 transition" />
                </Link>
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-sky-700 text-white rounded-br-sm" : "bg-slate-100 text-slate-900 rounded-bl-sm"}`}>
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-sky-100" : "text-slate-500"}`}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2 items-end">
        <Textarea
          rows={2}
          placeholder="Écrivez un message…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1"
          data-testid="message-input"
        />
        <Button type="submit" disabled={busy || !draft.trim()} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="send-message">
          <Send className="w-4 h-4 mr-1" />Envoyer
        </Button>
      </form>

      {reportOpen && (
        <ReportDialog
          open={true}
          onClose={() => setReportOpen(false)}
          targetType={data.partner.role === "mns" ? "mns" : "client"}
          targetId={data.partner.user_id}
          targetLabel={data.partner.name}
        />
      )}
    </div>
  );
}
